import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { api, setToken } from "../api";
import { useAuth, roleHome } from "../AuthContext";
import Brand from "../../components/site/Brand";

/**
 * Reset Password — lands here from the "Set a new password" email link.
 * Validates the token on mount, shows the account it's tied to, then
 * collects a new password. On success we auto-sign-in via the AuthOut
 * response returned by the backend.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState({ status: "loading", detail: null, error: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setState({ status: "error", detail: null, error: "No reset token provided." });
      return;
    }
    (async () => {
      try {
        const data = await api.get(`/auth/reset-password/${encodeURIComponent(token)}`);
        setState({ status: "ready", detail: data, error: "" });
      } catch (e) {
        setState({ status: "error", detail: null, error: e.message });
      }
    })();
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const data = await api.post("/auth/reset-password", { token, password });
      setToken(data.access_token);
      await refresh();
      navigate(roleHome(data.user));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center px-5 py-10" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Brand tone="dark" />
        </div>

        <div className="bg-white rounded-2xl shadow-[0_20px_50px_-25px_rgba(11,18,32,0.25)] border border-slate-200 p-8">
          {state.status === "loading" && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={14} className="animate-spin" /> Checking your reset link…
            </div>
          )}

          {state.status === "error" && (
            <div data-testid="reset-password-error-block">
              <div className="text-[11px] uppercase tracking-[0.22em] text-rose-600 font-semibold">
                Link unavailable
              </div>
              <h1 className="mt-2 font-display text-2xl tracking-tight text-slate-900 font-semibold">
                We couldn't open this reset link.
              </h1>
              <p className="mt-2 text-slate-500 text-[13.5px] leading-relaxed">{state.error}</p>
              <div className="mt-5 flex gap-3">
                <Link
                  to="/app/forgot-password"
                  data-testid="reset-password-request-new"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0B1220] text-white px-4 py-2.5 text-sm font-semibold"
                >
                  Request a new link <ArrowRight size={14} />
                </Link>
                <Link
                  to="/app/login"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 text-slate-700 px-4 py-2.5 text-sm font-semibold"
                >
                  Sign in
                </Link>
              </div>
            </div>
          )}

          {state.status === "ready" && (
            <>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
                Reset password
              </div>
              <h1 className="mt-2 font-display text-2xl md:text-3xl tracking-tight text-slate-900 font-semibold">
                Set a new password{state.detail.name ? `, ${state.detail.name.split(" ")[0]}` : ""}.
              </h1>
              <dl className="mt-6 space-y-1.5 text-[12.5px]">
                <div className="flex justify-between border-b border-slate-100 pb-1.5">
                  <dt className="text-slate-500">Account</dt>
                  <dd className="text-slate-900 font-medium" data-testid="reset-password-email">{state.detail.email}</dd>
                </div>
              </dl>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">New password</span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-new"
                    placeholder="At least 8 characters"
                    className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Confirm password</span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm"
                    placeholder="Repeat your password"
                    className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
                  />
                </label>

                {error && (
                  <div data-testid="reset-password-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="reset-password-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-3 text-sm font-semibold"
                >
                  {busy ? "Setting password…" : (<>Set new password <ShieldCheck size={14} /></>)}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Remembered it?{" "}
          <Link to="/app/login" className="text-[#8C6B33] hover:text-[#0B1220] font-semibold">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
